<template>
    <vx-modal
        :preventClosing="preventClosing"
        :stacked="stacked"
        :visible="props.visible"
        @update:visible="emit('update:visible', $event)"
        :padding="false"
        size="lg"
    >
        <template v-slot:header>
            <div class="px-6 pt-6">Preview Agent Response</div>
        </template>

        <template v-slot:subheader>
            <div class="px-6 text-slate-500">Type a test message below and see how the agent will validate it.</div>
        </template>

        <div class="p-6 space-y-4">
            <div v-if="hasValidated" class="space-y-3" data-test="validation-results">
                <div v-if="violations.length > 0" class="space-y-2" data-test="violations-list">
                    <vx-alert
                        v-for="(violation, index) in violations"
                        :key="index"
                        :color="props.validation === 'block' ? 'danger' : 'info'"
                        :closeable="false"
                    >
                        <template v-slot:icon>
                            <font-awesome-icon
                                :icon="props.validation === 'block' ? faHexagonExclamation : faTriangleExclamation"
                                widthAuto
                            ></font-awesome-icon>
                        </template>

                        {{ violation }}

                        <template v-slot:right>
                            <div class="flex-nowrap flex items-center gap-1">
                                <vx-badge
                                    size="2xs"
                                    class="ml-1 whitespace-nowrap"
                                    :color="props.validation === 'warn' ? 'info' : 'danger'"
                                >
                                    AI <font-awesome-icon :icon="faSparkle" />
                                </vx-badge>

                                <vx-badge
                                    size="2xs"
                                    class="ml-1 whitespace-nowrap"
                                    :color="props.validation === 'warn' ? 'info' : 'danger'"
                                >
                                    Set by Corp <font-awesome-icon :icon="faCrown" />
                                </vx-badge>
                            </div>
                        </template>
                    </vx-alert>
                </div>

                <vx-alert v-else color="success" :closeable="false" data-test="success-message">
                    <template v-slot:icon>
                        <font-awesome-icon :icon="faCircleCheck" widthAuto></font-awesome-icon>
                    </template>
                    Message passes all validation rules.
                </vx-alert>
            </div>

            <div class="relative">
                <message-builder ref="messageBuilderRef" preview v-model="messageBody" disableMedia></message-builder>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row w-full">
                <vx-button
                    @click="prefillPastMessage"
                    color="muted"
                    size="lg"
                    :loading="prefilling"
                    data-test="prefill-button"
                >
                    Prefill with Past Message
                </vx-button>
                <vx-button
                    @click="runValidation"
                    color="primary"
                    size="lg"
                    class="grow"
                    :loading="loading"
                    data-test="run-validation-button"
                >
                    Run Validation
                    <font-awesome-icon :icon="faMemoCircleCheck" />
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup lang="ts">
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import { VxModal, VxButton, VxAlert, VxBadge } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faTriangleExclamation,
    faHexagonExclamation,
    faSparkle,
    faCircleCheck,
    faMemoCircleCheck,
    faCrown,
} from '@fortawesome/pro-solid-svg-icons';
import { ref, watch, useTemplateRef, computed } from 'vue';
import { aiMessageVettingAgentsClient, campaignsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import debounce from '~/utils/debounce';

const teamId = Spark.state.currentTeam.id;

const props = defineProps<{
    visible: boolean;
    instructions: string;
    validation: 'warn' | 'block';
}>();

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
}>();

const toasted = useToasted();

const messageBody = ref('');
const loading = ref(false);
const prefilling = ref(false);
const hasValidated = ref(false);
const validatedMessage = ref('');
const violations = ref<string[]>([]);

const cachedCampaigns = ref<{ id: number; name: string }[]>([]);

const resetState = () => {
    messageBody.value = '';
    hasValidated.value = false;
    validatedMessage.value = '';
    violations.value = [];
    cachedCampaigns.value = [];
};

watch(
    () => props.visible,
    (visible) => {
        if (!visible) {
            resetState();
        }
    }
);

const runValidation = async () => {
    if (loading.value) {
        return;
    }

    if (!messageBody.value) {
        toasted.error('Please enter a message to validate before running validation.');
        return;
    }

    loading.value = true;

    try {
        const response = await aiMessageVettingAgentsClient.testAgent(
            { teamId },
            {
                instructions: props.instructions,
                message_body: messageBody.value,
            }
        );

        validatedMessage.value = messageBody.value;
        violations.value = response.data.violations;
        hasValidated.value = true;
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        loading.value = false;
    }
};

const prefillPastMessage = async () => {
    if (prefilling.value) {
        return;
    }

    prefilling.value = true;

    try {
        if (cachedCampaigns.value.length === 0) {
            const response = await campaignsClient.campaignsGetRecords(teamId, {
                statuses: ['completed', 'published'],
                per_page: 20,
            });
            cachedCampaigns.value = response.data.data || [];
        }

        if (cachedCampaigns.value.length === 0) {
            toasted.info('No past campaigns found to prefill from.');
            return;
        }

        const randomIndex = Math.floor(Math.random() * cachedCampaigns.value.length);
        const campaign = cachedCampaigns.value[randomIndex];

        if (!campaign) {
            toasted.info('No past campaigns found to prefill from.');
            return;
        }

        const campaignResponse = await campaignsClient.campaignGet(teamId, campaign.id);
        const campaignMessages = campaignResponse.data.campaign_messages || [];

        if (campaignMessages.length > 0 && campaignMessages[0].body) {
            messageBody.value = campaignMessages[0].body;
            hasValidated.value = false;
        } else {
            toasted.info('Selected campaign has no message body.');
        }
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        prefilling.value = false;
    }
};

const messageBuilderRef = useTemplateRef<InstanceType<typeof MessageBuilder>>('messageBuilderRef');
const stacked = computed(() => {
    if (!messageBuilderRef.value) {
        return false;
    }
    return messageBuilderRef.value.modalOpen;
});

const preventClosing = ref(false);

watch(
    [() => messageBuilderRef.value?.emojisPicker, () => messageBuilderRef.value?.actionsDropdown],
    debounce(() => {
        preventClosing.value = messageBuilderRef.value?.emojisPicker || messageBuilderRef.value?.actionsDropdown;
    }, 150)
);
</script>
